import type { StationsTextEntry } from "./types";
import type { FilterState, SortState, PaginationState } from "./types";

export function filterEntries(entries: StationsTextEntry[], filters: FilterState): StationsTextEntry[] {
	let result = entries;

	if (filters.search) {
		const q = filters.search.toLowerCase();
		result = result.filter(
			(e) =>
				e.key.toLowerCase().includes(q) ||
				e.currentText.toLowerCase().includes(q) ||
				e.defaultText.toLowerCase().includes(q) ||
				Object.values(e.translations || {}).some((text) =>
					String(text || "")
						.toLowerCase()
						.includes(q)
				)
		);
	}

	if (filters.translationState && filters.translationLang) {
		const lang = filters.translationLang;
		result = result.filter((e) => {
			const has = Boolean(String(e.translations?.[lang] || "").trim());
			return filters.translationState === "present" ? has : !has;
		});
	}

	if (filters.screen) {
		result = result.filter((e) => e.screen === filters.screen);
	}

	if (filters.group) {
		result = result.filter((e) => e.group === filters.group);
	}

	if (filters.tag) {
		result = result.filter((e) => {
			const tags = (e as any).tags as string[] | undefined;
			return Array.isArray(tags) && tags.includes(filters.tag);
		});
	}

	if (filters.status) {
		result = result.filter((e) => e.status === filters.status);
	}

	return result;
}

export function sortEntries(entries: StationsTextEntry[], sort: SortState): StationsTextEntry[] {
	const { column, dir } = sort;
	const factor = dir === "asc" ? 1 : -1;

	return [...entries].sort((a, b) => {
		let va: string | number = "";
		let vb: string | number = "";

		if (column === "key") {
			va = a.key;
			vb = b.key;
		} else if (column === "screen") {
			va = a.screen;
			vb = b.screen;
		} else if (column === "group") {
			va = a.group;
			vb = b.group;
		} else if (column === "currentText") {
			va = a.currentText;
			vb = b.currentText;
		} else if (column.startsWith("translation:")) {
			const lang = column.slice("translation:".length);
			va = String(a.translations?.[lang] || "");
			vb = String(b.translations?.[lang] || "");
		} else if (column === "status") {
			va = a.status;
			vb = b.status;
		} else if (column === "updatedAt") {
			va = a.updatedAt || "";
			vb = b.updatedAt || "";
		}

		if (va < vb) return -1 * factor;
		if (va > vb) return 1 * factor;
		return 0;
	});
}

export function paginateEntries<T>(entries: T[], pagination: PaginationState): T[] {
	const { page, pageSize } = pagination;
	const start = page * pageSize;
	return entries.slice(start, start + pageSize);
}

export function getUniqueScreens(entries: StationsTextEntry[]): string[] {
	return Array.from(new Set(entries.map((e) => e.screen))).sort();
}

export function getUniqueGroups(entries: StationsTextEntry[], screen?: string): string[] {
	const filtered = screen ? entries.filter((e) => e.screen === screen) : entries;
	return Array.from(new Set(filtered.map((e) => e.group))).sort();
}

export function getUniqueTags(entries: StationsTextEntry[]): string[] {
	const tags = new Set<string>();
	for (const entry of entries) {
		const entryTags = (entry as any).tags as string[] | undefined;
		if (Array.isArray(entryTags)) {
			for (const t of entryTags) {
				tags.add(t);
			}
		}
	}
	return Array.from(tags).sort();
}

/**
 * Effective text shown to a viewer for a given entry. When `currentText` is empty
 * the row renders `defaultText` with a "DEFAULT" hint, so Find should match there too.
 */
export function getEffectiveText(entry: StationsTextEntry): string {
	return entry.currentText || entry.defaultText;
}

export function countFindMatches(
	entries: StationsTextEntry[],
	find: string,
	matchMode: "substring" | "wholeword" | "regex"
): number {
	if (!find) return 0;
	let count = 0;
	for (const entry of entries) {
		if (matchesText(getEffectiveText(entry), find, matchMode)) count++;
	}
	return count;
}

export function countFindMatchesBreakdown(
	entries: StationsTextEntry[],
	find: string,
	matchMode: "substring" | "wholeword" | "regex"
): { overridden: number; defaults: number } {
	if (!find) return { overridden: 0, defaults: 0 };
	let overridden = 0;
	let defaults = 0;
	for (const entry of entries) {
		if (entry.currentText) {
			if (matchesText(entry.currentText, find, matchMode)) overridden++;
		} else if (entry.defaultText) {
			if (matchesText(entry.defaultText, find, matchMode)) defaults++;
		}
	}
	return { overridden, defaults };
}

export function matchesText(text: string, find: string, matchMode: "substring" | "wholeword" | "regex"): boolean {
	if (!find) return false;
	try {
		if (matchMode === "regex") {
			return new RegExp(find, "i").test(text);
		}
		if (matchMode === "wholeword") {
			return new RegExp(`\\b${escapeRegex(find)}\\b`, "i").test(text);
		}
		return text.toLowerCase().includes(find.toLowerCase());
	} catch {
		return false;
	}
}

function escapeRegex(str: string): string {
	return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function applyFindReplace(
	text: string,
	find: string,
	replace: string,
	matchMode: "substring" | "wholeword" | "regex"
): string {
	if (!find) return text;
	try {
		if (matchMode === "regex") {
			return text.replace(new RegExp(find, "gi"), replace);
		}
		if (matchMode === "wholeword") {
			return text.replace(new RegExp(`\\b${escapeRegex(find)}\\b`, "gi"), replace);
		}
		return text.split(new RegExp(escapeRegex(find), "gi")).join(replace);
	} catch {
		return text;
	}
}
